import { useBrand } from "../brand/BrandContext";
import { Page, SectionBlock } from "../components/Page";

// Simple contrast checker: relative luminance per WCAG.
function luminance(hex: string): number {
  const m = hex.replace("#", "");
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const toLin = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

function contrast(bg: string, fg: string): number {
  const a = luminance(bg);
  const b = luminance(fg);
  const [lo, hi] = a < b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

interface ContrastRating {
  label: string;
  tone: "ok" | "mid" | "warn";
}

function ratingLabel(ratio: number): ContrastRating {
  if (ratio >= 7) {
    return { label: "AAA", tone: "ok" };
  }
  if (ratio >= 4.5) {
    return { label: "AA", tone: "ok" };
  }
  if (ratio >= 3) {
    return { label: "AA large", tone: "mid" };
  }
  return { label: "Fails", tone: "warn" };
}

export function Colour() {
  const { variant } = useBrand();
  const p = variant.palette;

  const swatches = [
    {
      name: "Background",
      hex: p.bg,
      role: "Page backdrop. Holds the whole brand.",
    },
    {
      name: "Paper",
      hex: p.paper,
      role: "Cards, lifted surfaces, slight contrast above bg.",
    },
    { name: "Ink", hex: p.ink, role: "All body text and filled CTAs." },
    {
      name: "Ink soft",
      hex: p.inkSoft,
      role: "Secondary text. Captions, supporting copy.",
    },
    {
      name: "Amber",
      hex: p.amber,
      role: "Primary accent. Italic emphasis, the अ glyph, rules.",
    },
    {
      name: "Amber light",
      hex: p.amberLight,
      role: "Hover states, accents on ink backgrounds.",
    },
    {
      name: "Peach",
      hex: p.peach,
      role: "Soft chips, warm surfaces, accents.",
    },
    {
      name: "Peach deep",
      hex: p.peachDeep,
      role: "The 'Just ten minutes' pill, online stamp.",
    },
  ];

  const pairs = [
    ["Ink on background", p.bg, p.ink],
    ["Ink soft on background", p.bg, p.inkSoft],
    ["Amber on background", p.bg, p.amber],
    ["Paper on ink", p.ink, p.paper],
    ["Amber on ink", p.ink, p.amber],
    ["Ink on amber", p.amber, p.ink],
  ] as const;

  return (
    <Page
      number="05"
      title="Colour"
      eyebrow="The palette"
      intro={
        <>
          The palette is warm-led. Background carries the temperature; ink
          carries everything else. Amber is the only voice we ever raise. Avoid
          stacking light tints on light tints — let surfaces have one clear
          hierarchy.
        </>
      }
    >
      <SectionBlock title="Core swatches">
        <div className="grid gap-3 md:grid-cols-4">
          {swatches.map((s) => {
            const isDark = luminance(s.hex) < 0.45;
            return (
              <div
                key={s.name}
                className="flex flex-col gap-4 rounded-2xl p-5"
                style={{
                  background: s.hex,
                  color: isDark ? p.paper : p.ink,
                }}
              >
                <div className="aspect-square w-full" />
                <div>
                  <p className="font-display text-[18px]">{s.name}</p>
                  <p className="font-body text-[11px] tracking-[0.18em] uppercase opacity-80">
                    {s.hex.toUpperCase()}
                  </p>
                  <p className="mt-3 font-body text-[12px] leading-relaxed opacity-90">
                    {s.role}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </SectionBlock>

      <SectionBlock title="Contrast & accessibility">
        <div className="grid gap-3 md:grid-cols-2">
          {pairs.map(([label, bg, fg]) => {
            const ratio = contrast(bg, fg);
            const r = ratingLabel(ratio);
            return (
              <div
                key={label}
                className="flex items-center justify-between gap-4 rounded-xl p-4"
                style={{
                  background: bg,
                  color: fg,
                  border: "1px solid var(--rule)",
                }}
              >
                <div className="flex flex-col gap-1">
                  <span className="font-display text-[20px]">{label}</span>
                  <span className="font-body text-[12px] tracking-[0.18em] uppercase opacity-80">
                    {bg.toUpperCase()} → {fg.toUpperCase()}
                  </span>
                </div>
                <div className="text-right">
                  <span className="font-display text-[22px]">
                    {ratio.toFixed(2)}
                  </span>
                  <span
                    className="ml-2 rounded-full px-2 py-0.5 font-body text-[11px] font-semibold tracking-[0.12em] uppercase"
                    style={{
                      background:
                        r.tone === "ok"
                          ? "var(--amber)"
                          : r.tone === "mid"
                            ? "var(--peach-deep)"
                            : "#a23a2c",
                      color: r.tone === "ok" ? p.ink : p.ink,
                    }}
                  >
                    {r.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-6 font-body text-[13px] text-ink-soft">
          AA passes ≥ 4.5 for body text; AA large passes ≥ 3 for ≥18pt or bold
          ≥14pt; AAA ≥ 7. Anything failing should not carry body copy.
        </p>
      </SectionBlock>

      <SectionBlock title="Usage rules">
        <div className="grid gap-5 md:grid-cols-2">
          {[
            [
              "Background does the heaviest lifting",
              "It's the colour you see most. Push it slightly deeper than ‘cream’ — this is sand or walnut, not paper.",
            ],
            [
              "Amber is rare",
              "It earns its presence by appearing only in italic emphasis, the अ glyph, hairlines, and the calligraphic underline.",
            ],
            [
              "Two surface levels max",
              "Page bg + one lifted surface (paper). Don't stack three nested tints.",
            ],
            [
              "No pure white, no pure black",
              "Both feel sterile in this palette. Paper instead of white. Ink instead of black.",
            ],
            [
              "Peach is for moments",
              "Use peach-deep on one pill or one stamp per surface. Two becomes noise.",
            ],
            [
              "Mind the floor",
              "Surfaces fade warmer toward the bottom of a page (the floor warmth strip). Don't reset it to flat colour.",
            ],
          ].map(([t, b]) => (
            <div
              key={t}
              className="border-l-2 pl-5"
              style={{ borderColor: "var(--amber)" }}
            >
              <p className="font-display text-[20px] text-ink">{t}</p>
              <p className="mt-2 font-body text-[14px] leading-relaxed text-ink-soft">
                {b}
              </p>
            </div>
          ))}
        </div>
      </SectionBlock>
    </Page>
  );
}
